import CloseIcon from '@mui/icons-material/CloseOutlined';
import { Box, IconButton, Paper, Stack, Typography } from '@mui/material';

interface ClinicalCardFrameProps {
  title: string;
  /**
   * Drops the header's title text for hosts that already render the section
   * title themselves (the V3 patient page's section shell). Actions and the
   * close button keep their row; with neither, the header row is skipped.
   */
  hideTitle?: boolean;
  actions?: React.ReactNode;
  /**
   * 'embedded' drops the Paper chrome for dialog hosts and renders the ONE
   * header row the dialog shows: title, actions, and the close button — the
   * host omits its own DialogTitle entirely.
   */
  variant?: 'card' | 'embedded';
  /** Embedded only: renders the header's close button. */
  onClose?: () => void;
  children: React.ReactNode;
}

/**
 * Presentational stand-in for the shared V1 `BaseBlock` card chrome
 * (components/Patient/Blocks/BaseBlock): a bordered Paper with a bold title on
 * the left and an actions slot (filter + "+ Add") on the right. Domain-agnostic
 * on purpose — the Medications, Diagnoses, and Allergies card mocks all render
 * inside this one frame, mirroring how the real build passes controls to
 * BaseBlock's `actions` prop.
 */
export const ClinicalCardFrame: React.FC<ClinicalCardFrameProps> = ({
  title,
  hideTitle = false,
  actions,
  variant = 'card',
  onClose,
  children,
}) => {
  const showHeader = !hideTitle || Boolean(actions) || Boolean(onClose);

  if (variant === 'embedded') {
    return (
      <Box
        sx={{ display: 'flex', flexDirection: 'column', minHeight: 0, height: '100%' }}
      >
        {showHeader && (
          <Stack
            direction="row"
            spacing={1}
            sx={{ alignItems: 'center', px: 2, py: 1.5, flexWrap: 'wrap' }}
          >
            {hideTitle ? (
              <Box sx={{ flex: 1 }} />
            ) : (
              <Typography variant="h6" noWrap sx={{ fontWeight: 700, flex: 1, minWidth: 120 }}>
                {title}
              </Typography>
            )}
            {actions && (
              <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                {actions}
              </Stack>
            )}
            {onClose && (
              <IconButton aria-label="close" onClick={onClose} sx={{ color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            )}
          </Stack>
        )}
        {/* Children own their scrolling so a card can pin sections (the
            medications history footer) outside the scroll area. */}
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          {children}
        </Box>
      </Box>
    );
  }

  return (
    <Paper
      elevation={1}
      sx={{
        maxWidth: 1040,
        mx: 'auto',
        // BaseBlock's inter-module rhythm on Patient Page V3.
        mb: 2,
        borderRadius: '0.75rem',
        border: '1px solid',
        borderColor: 'divider',
        overflow: 'hidden',
      }}
    >
      {showHeader && (
        <Stack
          direction="row"
          sx={{
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            px: 2,
            py: 1.5,
            flexWrap: 'wrap',
          }}
        >
          {hideTitle ? (
            <Box />
          ) : (
            <Typography variant="h6" noWrap sx={{ fontWeight: 700 }}>
              {title}
            </Typography>
          )}
          {actions && (
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              {actions}
            </Stack>
          )}
        </Stack>
      )}
      {children}
    </Paper>
  );
};
